import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import PlusIconWhite from "../../images/PlusIconWhite";
import { getAllSupplierInventory } from "../../services/supplier.service";
import ViewIcon from "../../images/ViewIcon";
import { useResponsive } from "../../hook/useResponsive";
import {
    ListColumn,
    ListEmptyState,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    Pill,
    cells,
    t,
} from '../../components/list';

interface SupplierRow {
    id: string;
    companyName: string;
    emailAddress: string | null;
    city: string | null;
    state: string | null;
    supplierInventoryCount: number;
}
interface SupplierInventoryResponse {
    data: {
        data: {
            supplierInventories: SupplierRow[];
            count: number
        }
    }
}

const SupplierInventory = () => {
    const preLanguage = localStorage.getItem("language");
    const { isMobile } = useResponsive();
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [language, setLanguage] = useState<any>(null);

    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'f' && searchInputRef.current) {
                event.preventDefault();
                searchInputRef.current.focus();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    /** One page of suppliers; the search box maps onto `searchValue` (company name). */
    const fetchSuppliers = async (query: ListQuery) => {
        webLoadingFalse();
        // The API trims searchValue unconditionally, so it must always be sent.
        const response: SupplierInventoryResponse = await getAllSupplierInventory(
            query.page,
            query.limit,
            query.search ?? ''
        );
        return {
            rows: response.data.data.supplierInventories ?? [],
            count: response.data.data.count ?? 0,
        };
    };

    const goToSupplier = (id: string) => webNavigate(`/${preLanguage}/supplierInventory/${id}`);

    const emptyState: ListEmptyState = {
        title: language?.['No Supplier Inventory Found'] ?? 'No Supplier Inventory Found',
        desc: language?.['Please Create Inventory Supplier'] ?? 'Please Create Inventory Supplier',
    };

    /* ---------------- List kit configuration ---------------- */

    const columns: ListColumn<SupplierRow>[] = [
        {
            key: 'companyName',
            label: 'Supplier',
            width: 280,
            minWidth: 140,
            hideable: false,
            render: cells.primary(s => s.companyName),
            mobile: 'title',
            onClick: s => goToSupplier(String(s.id)),
        },
        {
            key: 'emailAddress',
            label: 'Email address',
            width: 260,
            minWidth: 120,
            value: s => s.emailAddress ?? '',
            mobile: 'subtitle',
        },
        {
            key: 'city',
            label: 'Location',
            width: 220,
            minWidth: 100,
            value: s => [s.city, s.state].filter(Boolean).join(', '),
            mobile: 'subtitle',
        },
        {
            key: 'supplierInventoryCount',
            label: 'Products',
            width: 140,
            render: (s, ctx) => (
                <Pill
                    label={`${Number(s.supplierInventoryCount ?? 0)} ${t(ctx.language, 'products', 'products')}`}
                    kind="off"
                />
            ),
            mobile: 'trailing',
        },
    ];

    const rowActions: ListRowAction<SupplierRow>[] = [
        {
            key: 'view',
            label: 'View',
            icon: <ViewIcon />,
            sheetIcon: 'chev',
            onRun: s => goToSupplier(String(s.id)),
        },
    ];

    return (
        <div
            className="h-full relative"
            style={{
                overflowY: isMobile ? "auto" : undefined,
                paddingBottom: isMobile ? "24px" : undefined,
                display: isMobile ? undefined : 'flex',
                flexDirection: isMobile ? undefined : 'column',
            }}
        >
            <div className="lg:flex items-center justify-between">
                <p
                    style={{ color: '#222525' }}
                    className="text-[24px] leading-[130%] font-medium font-inter"
                >
                    {language?.['Suppliers'] ?? 'Suppliers'}
                </p>
                <button
                    type="button"
                    className="cursor-pointer flex items-center justify-center"
                    style={{
                        border: 'none',
                        font: 'inherit',
                        gap: '8px',
                        padding: "8px 24px 8px 16px",
                        backgroundColor: '#0F7679',
                        borderRadius: '30px',
                        width: 'fit-content',
                        marginTop: isMobile ? '20px' : '0px',
                    }}
                    onClick={() => {
                        webNavigate('/' + preLanguage + '/clients/new');
                    }}
                >
                    <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                        <PlusIconWhite />
                    </div>
                    <p
                        style={{ color: '#FFFFFF' }}
                        className="text-[14px] leading-[130%] font-medium font-inter"
                    >
                        {language?.['Add supplier'] ?? 'Add supplier'}
                    </p>
                </button>
            </div>
            <ListScreen
                ref={listRef}
                language={language}
                columns={columns}
                fetchRows={fetchSuppliers}
                searchPlaceholder="Search suppliers"
                searchInputRef={searchInputRef}
                rowActions={rowActions}
                countNoun="suppliers"
                emptyState={emptyState}
            />
        </div>
    )
};

export default SupplierInventory;
